<script>
  import moment from 'moment';
  import DatePicker from 'vue2-datepicker';
  import conciliacaoProjetoServico from '../servico';
  import BlocoOperacao from './BlocoOperacao.vue';

  const COLUNAS = [
    { chave: 'cr', texto: 'CR', valor: c => +c.idLancamento || 0 },
    { chave: 'cliente', texto: 'Cliente', valor: c => (c.nomePessoa || c.historico || '').toUpperCase() },
    { chave: 'documento', texto: 'Documento', valor: c => ([c.documento, c.documentoReceberPagar].filter(t => !!t)[0] || '').toUpperCase() },
    { chave: 'nota', texto: 'Nota fiscal', valor: c => (c.notaFiscal || '').toUpperCase() },
    { chave: 'vencimento', texto: 'Vencimento', valor: c => +c.dataInt || 0 },
    { chave: 'conta', texto: 'Conta atual', valor: c => (c.nomeConta || '').toUpperCase() },
    { chave: 'valor', texto: 'Valor', valor: c => +c.valor || 0, classe: 'crv-dir' },
  ];
  const ORDEM_PADRAO = { coluna: 'vencimento', crescente: true };
  const ESCOPOS = [
    { valor: 0, texto: 'Todas as contas' },
    { valor: 2, texto: 'Fora da TRA' },
    { valor: 1, texto: 'Já na TRA' },
  ];

  export function somaContasReceber(lista) {
    return Math.round((lista || []).reduce((s, c) => s + (+c.valor || 0), 0) * 100) / 100;
  }

  export default {
    components: { DatePicker, BlocoOperacao },
    props: {
      value: { type: Array, default: () => [] },
      idContaCript: { type: String, required: true },
      total: { type: Number, default: 0 },
      buscaInicial: { type: String, default: '' },
      sugestoes: { type: Array, default: () => [] },
      nomeTransitoria: { type: String, default: 'conta transitória (TRA)' },
      validar: { type: Boolean, default: false },
    },
    data() {
      return {
        aberto: false,
        carregando: false,
        erro: '',
        lista: null,
        marcados: [],
        filtro: { busca: '', escopo: 0, dataIni: null, dataFim: null, valorTexto: '', soComNotaFiscal: false },
        temporizador: null,
        pedido: 0,
        buscaAutomatica: '',
        ordem: Object.assign({}, ORDEM_PADRAO),
      }
    },
    computed: {
      sugestoesBusca() {
        const lista = [];
        [this.buscaInicial].concat(this.sugestoes || []).forEach(s => {
          const t = (s || '').trim();
          if (t && !lista.some(x => x.toUpperCase() === t.toUpperCase())) lista.push(t);
        });
        return lista;
      },
      escopos() { return ESCOPOS; },
      somaVinculados() { return somaContasReceber(this.value); },
      somaMarcados() { return somaContasReceber(this.marcados); },
      bateVinculados() { return Math.abs(this.somaVinculados - this.total) < 0.005; },
      bateMarcados() { return Math.abs(this.somaMarcados - this.total) < 0.005; },
      faltaVinculados() { return Math.round((this.total - this.somaVinculados) * 100) / 100; },
      faltaMarcados() { return Math.round((this.total - this.somaMarcados) * 100) / 100; },
      mostraErro() { return this.validar && this.value.length > 0 && !this.bateVinculados; },
      colunas() { return COLUNAS; },
      listaOrdenada() {
        const coluna = COLUNAS.find(c => c.chave === this.ordem.coluna) || COLUNAS[4];
        const sentido = this.ordem.crescente ? 1 : -1;
        return (this.lista || []).slice().sort((a, b) => {
          const va = coluna.valor(a);
          const vb = coluna.valor(b);
          const r = typeof va === 'number' ? va - vb : va.localeCompare(vb, 'pt-BR');
          return (r || (+a.dataInt || 0) - (+b.dataInt || 0) || (+a.idLancamento || 0) - (+b.idLancamento || 0)) * sentido;
        });
      },
      valorFiltro() {
        const texto = (this.filtro.valorTexto || '').trim();
        if (!texto) return 0;
        const numero = +texto.replace(/\./g, '').replace(',', '.');
        return isNaN(numero) ? 0 : numero;
      },
      valorTotalAtivo() { return !!this.valorFiltro && Math.abs(this.valorFiltro - this.total) < 0.005; },
      filtroAtivo() {
        const f = this.filtro;
        return !!(f.busca.trim() || f.escopo || f.dataIni || f.dataFim || this.valorFiltro || f.soComNotaFiscal);
      },
    },
    watch: {
      filtro: {
        deep: true,
        handler() {
          if (!this.aberto) return;
          clearTimeout(this.temporizador);
          this.temporizador = setTimeout(() => this.buscar(), 400);
        },
      },
    },
    beforeDestroy() {
      clearTimeout(this.temporizador);
    },
    methods: {
      formatar(v) {
        return (+v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      abrir() {
        this.marcados = this.value.slice();
        this.lista = null;
        this.erro = '';
        this.filtro = { busca: this.sugestoesBusca[0] || '', escopo: 0, dataIni: null, dataFim: null, valorTexto: '', soComNotaFiscal: false };
        this.buscaAutomatica = this.filtro.busca;
        this.ordem = Object.assign({}, ORDEM_PADRAO);
        this.aberto = true;
        this.buscar();
        this.$nextTick(() => this.$refs.busca && this.$refs.busca.focus());
      },
      fechar() {
        clearTimeout(this.temporizador);
        this.aberto = false;
      },
      limparFiltros() {
        this.filtro = { busca: '', escopo: 0, dataIni: null, dataFim: null, valorTexto: '', soComNotaFiscal: false };
      },
      alternarValorTotal() {
        this.filtro.valorTexto = this.valorTotalAtivo ? '' : this.formatar(this.total);
      },
      sugestaoAtiva(s) {
        return this.filtro.busca.trim().toUpperCase() === s.toUpperCase();
      },
      alternarSugestao(s) {
        this.buscaAutomatica = '';
        this.filtro.busca = this.sugestaoAtiva(s) ? '' : s;
      },
      buscarAgora() {
        clearTimeout(this.temporizador);
        this.buscar();
      },
      enterNoFiltro(e) {
        if (!e.target || e.target.tagName !== 'INPUT') return;
        e.preventDefault();
        e.stopPropagation();
        this.buscarAgora();
      },
      buscar() {
        const pedido = ++this.pedido;
        const f = this.filtro;

        this.carregando = true;
        this.erro = '';

        conciliacaoProjetoServico.contasReceberAbertasVinculo(this.idContaCript, {
          busca: f.busca.trim(),
          valor: this.valorFiltro,
          escopo: f.escopo,
          soComNotaFiscal: f.soComNotaFiscal,
          dataIni: f.dataIni ? moment(f.dataIni).format('YYYY-MM-DD') : '',
          dataFim: f.dataFim ? moment(f.dataFim).format('YYYY-MM-DD') : '',
        })
          .then(lista => {
            if (pedido !== this.pedido) return;
            this.carregando = false;
            this.lista = lista || [];
            this.tentarProximaSugestao(f.busca.trim());
          })
          .catch(res => {
            if (pedido !== this.pedido) return;
            this.carregando = false;
            this.erro = (res && res.body && res.body.message) || 'Não foi possível buscar os contas a receber.';
          });
      },
      tentarProximaSugestao(busca) {
        if (!this.buscaAutomatica || busca !== this.buscaAutomatica) return;
        if (this.lista.length > 0) { this.buscaAutomatica = ''; return; }
        const i = this.sugestoesBusca.indexOf(busca);
        const proxima = i >= 0 ? this.sugestoesBusca[i + 1] : '';
        this.buscaAutomatica = proxima || '';
        if (proxima) this.filtro.busca = proxima;
      },
      ordenarPor(chave) {
        if (this.ordem.coluna === chave) this.ordem.crescente = !this.ordem.crescente;
        else this.ordem = { coluna: chave, crescente: true };
      },
      iconeOrdem(chave) {
        if (this.ordem.coluna !== chave) return 'fa fa-sort crv-ord-inativo';
        return this.ordem.crescente ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
      },
      marcado(c) { return this.marcados.some(m => m.idLancamento === c.idLancamento); },
      semConta(c) { return (c.projetosSemContaTransitoria || '').trim(); },
      mensagemSemConta(lista) {
        const projetos = [];
        lista.forEach(c => this.semConta(c).split(', ').forEach(p => { if (p && projetos.indexOf(p) < 0) projetos.push(p); }));
        return (projetos.length > 1 ? 'Os projetos ' : 'O projeto ') + projetos.join(', ')
          + (projetos.length > 1 ? ' não têm' : ' não tem') + ' a conta ' + this.nomeTransitoria
          + ' cadastrada. Cadastre a conta no projeto antes de vincular.';
      },
      alternar(c) {
        if (this.semConta(c) && !this.marcado(c)) {
          toastr.warning(this.mensagemSemConta([c]));
          return;
        }
        const indice = this.marcados.findIndex(m => m.idLancamento === c.idLancamento);
        if (indice >= 0) this.marcados.splice(indice, 1);
        else this.marcados.push(c);
      },
      remover(c) {
        this.$emit('input', this.value.filter(v => v.idLancamento !== c.idLancamento));
      },
      documentoDe(c) {
        return [c.documento, c.documentoReceberPagar].filter(t => !!t)[0] || '';
      },
      vincular() {
        const semConta = this.marcados.filter(c => this.semConta(c));

        if (semConta.length) {
          toastr.error(this.mensagemSemConta(semConta));
          return;
        }

        const mudam = this.marcados.filter(c => +c.crOutraConta === 1);

        if (!mudam.length) {
          this.$emit('input', this.marcados.slice());
          this.fechar();
          return;
        }

        const contas = mudam.map(c => 'CR ' + c.idLancamento + ' (' + (c.nomeConta || 'outra conta') + ')').join(', ');
        const varios = mudam.length > 1;

        this.$swal({
          title: 'Vincular contas a receber',
          text: 'Quando o processo completar, ' + (varios ? 'estes contas a receber serão baixados' : 'este contas a receber será baixado')
              + ' na ' + this.nomeTransitoria + ' e ' + (varios ? 'mudam' : 'muda') + ' de conta: ' + contas + '. Deseja continuar?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, vincular',
          cancelButtonText: 'Cancelar',
        })
          .then(result => {
            if (!(result === true || (result && result.value))) return;
            this.$emit('input', this.marcados.slice());
            this.fechar();
          })
          .catch(() => {});
      },
    },
  }
</script>

<template>
  <div class="crv">
    <bloco-operacao selo="CR" cor="cre" titulo="Contas a receber vinculados"
                    :subtitulo="'Títulos que já existem e serão baixados na ' + nomeTransitoria + ' quando a transferência concluir'"
                    nota="Opcional. Sem título vinculado, o débito da TRA fica pendente e pode ser regularizado depois pela própria TRA, onde dá para criar o contas a receber.">
      <button slot="acao" type="button" class="crv-buscar" @click="abrir">
        <i class="fa fa-search"></i> Buscar contas a receber
      </button>

      <div v-if="!value.length" class="crv-vazio">
        Nenhum contas a receber vinculado. Clique em <b>Buscar contas a receber</b> para escolher um ou mais títulos em aberto.
      </div>

      <template v-else>
        <div class="crv-lista">
          <div v-for="c in value" :key="c.idLancamento" class="crv-item">
            <div class="crv-item-txt">
              <div class="crv-l1">
                <b>CR {{ c.idLancamento }}</b>
                <span class="crv-nome" :title="c.nomePessoa || c.historico">{{ c.nomePessoa || c.historico || 'Sem cliente' }}</span>
                <span class="crv-valor mono">R$ {{ formatar(c.valor) }}</span>
              </div>
              <div class="crv-l2">
                <span v-if="c.cpfcnpj" class="mono">{{ c.cpfcnpj }}</span>
                <span v-if="documentoDe(c)" class="mono">{{ documentoDe(c) }}</span>
                <span v-if="c.notaFiscal" class="mono"><i class="fa fa-file-text-o"></i> {{ c.notaFiscal }}<template v-if="c.notaFiscalEmissao"> · emissão {{ c.notaFiscalEmissao }}</template></span>
                <span><i class="fa fa-calendar"></i> Venc. {{ c.dataStr }}</span>
                <span v-if="c.nomeProjeto" class="crv-proj" :title="c.nomeProjeto">{{ c.nomeProjeto }}</span>
              </div>
              <div class="crv-conta">
                <template v-if="+c.crOutraConta === 1">
                  <span class="crv-tag crv-tag-muda">{{ c.nomeConta || 'Outra conta' }}</span>
                  <i class="fa fa-long-arrow-right"></i>
                  <span class="crv-tag crv-tag-tra">{{ nomeTransitoria }}</span>
                  <small>muda de conta na baixa</small>
                </template>
                <span v-else class="crv-tag crv-tag-tra">Já na {{ nomeTransitoria }}</span>
              </div>
            </div>
            <button type="button" class="crv-remover" title="Desvincular" @click="remover(c)"><i class="fa fa-trash-o"></i></button>
          </div>
        </div>

        <div class="crv-total" :class="bateVinculados ? 'crv-ok' : 'crv-falta'">
          <span>Vinculado R$ {{ formatar(somaVinculados) }} de R$ {{ formatar(total) }}</span>
          <span v-if="bateVinculados"><i class="fa fa-check"></i> Bate com o valor</span>
          <span v-else-if="faltaVinculados > 0">Faltam R$ {{ formatar(faltaVinculados) }}</span>
          <span v-else>Passou R$ {{ formatar(-faltaVinculados) }}</span>
        </div>
        <span v-if="mostraErro" class="crv-msg">A soma dos contas a receber tem que ser igual ao valor.</span>
      </template>
    </bloco-operacao>

    <div v-if="aberto" class="crv-back">
      <div class="crv-modal">
        <div class="crv-cab">
          <h4><i class="fa fa-file-text-o"></i> Buscar contas a receber em aberto</h4>
          <small>Marque um ou mais títulos. Ficam de fora os que já estão em grupo de conciliação, na fila de baixa ou vinculados a outra regularização. Título com projeto sem a conta {{ nomeTransitoria }} aparece bloqueado.</small>
        </div>

        <div class="crv-filtros" @keydown.enter="enterNoFiltro">
          <div class="crv-filtro crv-filtro-lg">
            <label>Cliente, CPF/CNPJ, documento ou histórico</label>
            <div class="crv-busca-linha">
              <input ref="busca" type="text" class="form-control" v-model="filtro.busca" maxlength="100" placeholder="Digite para filtrar...">
              <button type="button" class="btn btn-success no-margins" :disabled="carregando" @click="buscarAgora" title="Buscar agora (Enter)">
                <i class="fa" :class="carregando ? 'fa-spinner fa-pulse' : 'fa-search'"></i> Buscar
              </button>
            </div>
          </div>
          <div class="crv-filtro crv-filtro-sm">
            <label>Vencimento de</label>
            <date-picker v-model="filtro.dataIni" format="DD/MM/YYYY" lang="pt-br" input-class="form-control" placeholder="Selecione"
                         :editable="false" :shortcuts="false" :append-to-body="true"
                         :popup-style="{ zIndex: 3600 }" style="width:100%"></date-picker>
          </div>
          <div class="crv-filtro crv-filtro-sm">
            <label>até</label>
            <date-picker v-model="filtro.dataFim" format="DD/MM/YYYY" lang="pt-br" input-class="form-control" placeholder="Selecione"
                         :editable="false" :shortcuts="false" :append-to-body="true"
                         :popup-style="{ zIndex: 3600 }" style="width:100%"></date-picker>
          </div>
          <div class="crv-filtro crv-filtro-sm">
            <label>Valor</label>
            <input type="text" class="form-control mono" v-model="filtro.valorTexto" maxlength="20" placeholder="0,00">
          </div>
          <div class="crv-escopo">
            <button v-for="e in escopos" :key="e.valor" type="button" :class="{ ativo: filtro.escopo === e.valor }"
                    @click="filtro.escopo = e.valor">{{ e.texto }}</button>
          </div>
          <div class="crv-chips">
            <button type="button" class="crv-chip" :class="{ ativo: valorTotalAtivo }" @click="alternarValorTotal">
              <i v-if="valorTotalAtivo" class="fa fa-check"></i> Valor da operação R$ {{ formatar(total) }}
            </button>
            <button v-for="s in sugestoesBusca" :key="'sug-' + s" type="button" class="crv-chip" :class="{ ativo: sugestaoAtiva(s) }" @click="alternarSugestao(s)">
              <i v-if="sugestaoAtiva(s)" class="fa fa-check"></i> Do extrato: {{ s }}
            </button>
            <button type="button" class="crv-chip" :class="{ ativo: filtro.soComNotaFiscal }" @click="filtro.soComNotaFiscal = !filtro.soComNotaFiscal">
              <i :class="filtro.soComNotaFiscal ? 'fa fa-check' : 'fa fa-file-text-o'"></i> Com nota fiscal vinculada
            </button>
            <button v-if="filtroAtivo" type="button" class="crv-chip crv-chip-limpar" @click="limparFiltros">
              <i class="fa fa-eraser"></i> Limpar filtros
            </button>
          </div>
        </div>

        <div class="crv-corpo">
          <div v-if="carregando && !lista" class="crv-info"><i class="fa fa-spinner fa-pulse"></i> Buscando os contas a receber...</div>
          <div v-else-if="erro" class="crv-info crv-erro">{{ erro }}</div>
          <div v-else-if="lista && !lista.length" class="crv-info">Nenhum contas a receber em aberto com esses filtros.</div>
          <template v-else-if="lista">
            <table class="crv-tab" :class="{ 'crv-tab-carregando': carregando }">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="col in colunas" :key="col.chave" class="crv-ord" :class="col.classe" @click="ordenarPor(col.chave)">
                    {{ col.texto }} <i :class="iconeOrdem(col.chave)"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in listaOrdenada" :key="c.idLancamento" :class="{ on: marcado(c), bloq: semConta(c) && !marcado(c) }" @click="alternar(c)">
                  <td><input type="checkbox" :checked="marcado(c)" :disabled="!!semConta(c) && !marcado(c)" @click.stop="alternar(c)"></td>
                  <td class="mono">{{ c.idLancamento }}</td>
                  <td class="crv-cel-nome">
                    <div :title="c.nomePessoa || c.historico">{{ c.nomePessoa || c.historico || '—' }}</div>
                    <small class="mono" v-if="c.cpfcnpj">{{ c.cpfcnpj }}</small>
                    <small v-if="c.nomeProjeto" :title="c.nomeProjeto">{{ c.nomeProjeto }}</small>
                    <small v-if="semConta(c)" class="crv-sem-conta" :title="semConta(c)">
                      <i class="fa fa-ban"></i> Sem a conta {{ nomeTransitoria }}: {{ semConta(c) }}
                    </small>
                  </td>
                  <td class="mono">{{ documentoDe(c) || '—' }}</td>
                  <td class="crv-cel-nf">
                    <template v-if="c.notaFiscal">
                      <span class="crv-tag crv-tag-nf mono">{{ c.notaFiscal }}</span>
                      <small v-if="c.notaFiscalEmissao" class="mono">Emissão {{ c.notaFiscalEmissao }}</small>
                      <small v-if="c.notaFiscalRPS" class="mono">RPS {{ c.notaFiscalRPS }}</small>
                      <small v-if="+c.notaFiscalValor && Math.abs(+c.notaFiscalValor - +c.valor) >= 0.005" class="mono">NF R$ {{ formatar(c.notaFiscalValor) }}</small>
                    </template>
                    <span v-else class="crv-sem-nf">—</span>
                  </td>
                  <td class="mono">{{ c.dataStr }}</td>
                  <td>
                    <span :class="+c.crOutraConta === 1 ? 'crv-tag crv-tag-muda' : 'crv-tag crv-tag-tra'">{{ c.nomeConta || '—' }}</span>
                  </td>
                  <td class="mono crv-dir">{{ formatar(c.valor) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>

        <div class="crv-rodape">
          <span class="crv-cont" :class="marcados.length ? (bateMarcados ? 'crv-ok' : 'crv-falta') : ''">
            <template v-if="marcados.length">
              {{ marcados.length }} marcado{{ marcados.length > 1 ? 's' : '' }} · R$ {{ formatar(somaMarcados) }} de R$ {{ formatar(total) }}
              <template v-if="bateMarcados"> · bate com o valor</template>
              <template v-else-if="faltaMarcados > 0"> · faltam R$ {{ formatar(faltaMarcados) }}</template>
              <template v-else> · passou R$ {{ formatar(-faltaMarcados) }}</template>
            </template>
            <template v-else>Nenhum título marcado</template>
          </span>
          <button type="button" class="btn btn-primary" :disabled="!marcados.length && !value.length" @click="vincular">
            <i class="fa fa-link"></i> Vincular
          </button>
          <button type="button" class="crv-sair" @click="fechar"><i class="fa fa-sign-out"></i> Sair</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .mono { font-family: 'IBM Plex Mono', Consolas, monospace; }
  .crv { margin-bottom: 12px; }
  .crv:last-child { margin-bottom: 0; }
  .crv-buscar { flex: none; height: 28px; padding: 0 11px; background: #fff; border: 1px solid #0f8a5f; border-radius: 5px; font-size: 12px; font-weight: 600; color: #0a6d4d; }
  .crv-buscar:hover { background: #e8f5ef; }
  .crv-sair { display: inline-flex; align-items: center; gap: 7px; background: #fdf1f1; border: 1px solid #e4d7d7; border-radius: 5px; padding: 6px 18px; font-size: 12.5px; font-weight: 600; color: #a13c3c; }
  .crv-sair:hover { background: #fbe6e6; }
  .crv-vazio { font-size: 12.5px; color: #6b747e; padding: 4px 2px; }
  .crv-lista { display: flex; flex-direction: column; gap: 7px; }
  .crv-item { display: flex; gap: 10px; align-items: flex-start; border: 1px solid #d9eee5; background: #f6fbf9; border-radius: 6px; padding: 8px 10px; }
  .crv-item-txt { flex: 1 1 auto; min-width: 0; }
  .crv-l1 { display: flex; gap: 10px; align-items: baseline; font-size: 13px; color: #1c2024; min-width: 0; }
  .crv-nome { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .crv-valor { flex: none; font-weight: 600; }
  .crv-l2 { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 11.5px; color: #6b747e; margin-top: 2px; }
  .crv-l2 .fa { color: #9aa3ac; }
  .crv-proj { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .crv-conta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 5px; font-size: 11.5px; color: #8a939c; }
  .crv-conta .fa { color: #9aa3ac; }
  .crv-tag { display: inline-block; font-size: 10.5px; font-weight: 600; border-radius: 9px; padding: 2px 8px; white-space: nowrap; }
  .crv-tag-muda { background: #fdf3e1; color: #8a5a00; }
  .crv-tag-tra { background: #e3f3ec; color: #0a6d4d; }
  .crv-remover { flex: none; border: 0; background: none; color: #b0bac5; cursor: pointer; padding: 2px 4px; font-size: 14px; }
  .crv-remover:hover { color: #c0392b; }
  .crv-total { display: flex; justify-content: space-between; gap: 10px; margin-top: 9px; padding: 7px 10px; border-radius: 5px; font-size: 12px; font-weight: 600; }
  .crv-ok { background: #e8f5ef; color: #0a6d4d; }
  .crv-falta { background: #fbeeee; color: #a13c3c; }
  .crv-msg { display: block; margin-top: 4px; font-size: 11.5px; color: #c0392b; }

  .crv-back { position: fixed; inset: 0; background: rgba(16,24,40,.5); z-index: 3400; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .crv-modal { background: #fff; border-radius: 8px; box-shadow: 0 18px 48px rgba(16,24,40,.28); width: 100%; max-width: 980px; max-height: 90vh; display: flex; flex-direction: column; }
  .crv-cab { padding: 16px 20px 12px; border-bottom: 1px solid #e6eaee; flex: 0 0 auto; }
  .crv-cab h4 { margin: 0 0 3px; font-size: 17px; font-weight: 600; color: #1c2024; }
  .crv-cab small { color: #8a939c; font-size: 12.5px; }
  .crv-filtros { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; padding: 12px 20px; border-bottom: 1px solid #eef1f4; background: #fafbfc; flex: 0 0 auto; }
  .crv-filtro { display: flex; flex-direction: column; flex: 1 1 130px; min-width: 0; }
  .crv-filtro-lg { flex: 3 1 260px; }
  .crv-filtro-sm { flex: 0 1 140px; }
  .crv-filtro label { font-size: 11.5px; font-weight: 600; color: #4b545e; margin-bottom: 3px; }
  .crv-filtro .form-control { height: 32px; font-size: 12.5px; }
  .crv-busca-linha { display: flex; gap: 6px; }
  .crv-busca-linha .form-control { flex: 1 1 auto; min-width: 0; }
  .crv-busca-linha .btn { height: 32px; padding: 0 12px; font-size: 12.5px; flex: 0 0 auto; }
  .crv-escopo { display: flex; border: 1px solid #d3d8de; border-radius: 6px; overflow: hidden; }
  .crv-escopo button { height: 30px; padding: 0 12px; border: none; background: #fff; color: #4b545e; font-size: 12.5px; font-weight: 600; }
  .crv-escopo button + button { border-left: 1px solid #d3d8de; }
  .crv-escopo button.ativo { background: #e8f5ef; color: #0a6d4d; }
  .crv-chips { display: flex; flex-wrap: wrap; gap: 6px; flex: 1 1 auto; }
  .crv-chip { height: 28px; padding: 0 10px; border: 1px dashed #b9c3cd; border-radius: 14px; background: #fff; color: #2b6cb0; font-size: 12px; font-weight: 600; }
  .crv-chip:hover { background: #f1f5f9; border-color: #2b6cb0; }
  .crv-chip.ativo { border-style: solid; border-color: #0f8a5f; background: #e8f5ef; color: #0a6d4d; }
  .crv-chip-limpar { color: #5b6570; }
  .crv-tag-nf { background: #eaf1fb; color: #1f5fa8; }
  .crv-cel-nf small { display: block; color: #8a939c; font-size: 11px; white-space: nowrap; margin-top: 2px; }
  .crv-sem-nf { color: #b0b7be; }
  .crv-corpo { padding: 12px 20px; overflow: auto; flex: 1 1 auto; min-height: 220px; }
  .crv-info { padding: 28px 10px; text-align: center; color: #8a939c; font-size: 13px; }
  .crv-erro { color: #a13c3c; }
  .crv-tab th.crv-ord { cursor: pointer; user-select: none; white-space: nowrap; }
  .crv-tab th.crv-ord:hover { color: #2b6cb0; }
  .crv-tab th.crv-ord .fa { margin-left: 3px; color: #2b6cb0; }
  .crv-tab th.crv-ord .crv-ord-inativo { color: #c5ccd3; }
  .crv-tab { width: 100%; border-collapse: collapse; font-size: 12.5px; color: #3b444d; }
  .crv-tab-carregando { opacity: .55; }
  .crv-tab th { position: sticky; top: 0; background: #fff; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #8a939c; text-align: left; padding: 6px 8px; border-bottom: 1px solid #e6e9ed; }
  .crv-tab td { padding: 7px 8px; border-bottom: 1px solid #f0f2f4; vertical-align: top; }
  .crv-tab tbody tr { cursor: pointer; }
  .crv-tab tbody tr:hover { background: #f7f9fa; }
  .crv-tab tbody tr.on { background: #eef8f3; }
  .crv-tab tbody tr.bloq { cursor: not-allowed; background: #fdf7f7; }
  .crv-tab tbody tr.bloq td { color: #8a939c; }
  .crv-tab .crv-sem-conta { color: #a13c3c; font-weight: 600; white-space: normal; }
  .crv-tab input { margin: 0; }
  .crv-cel-nome { max-width: 300px; }
  .crv-cel-nome div { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #1c2024; }
  .crv-cel-nome small { display: block; color: #8a939c; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .crv-dir { text-align: right; white-space: nowrap; }
  .crv-rodape { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid #e6eaee; flex: 0 0 auto; }
  .crv-cont { margin-right: auto; font-size: 12px; font-weight: 600; color: #6b747e; padding: 5px 9px; border-radius: 5px; }
</style>



// WEBPACK FOOTER //
// FrontEnd/modulos/conciliacao-projeto/componentes/ContasReceberVinculadas.vue